import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Wand2, Download, Lock, Crown, Plus, Youtube, Type, User, Package, Palette } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api, mediaUrl } from "@/lib/api";
import { ImageSlot, uploadThumbImage, abs } from "@/components/yt/ImageSlot";
import ThumbGallery from "@/components/yt/ThumbGallery";

const RATIO = { "16:9": "16/9", "9:16": "9/16", "1:1": "1/1", "4:5": "4/5", "4:3": "4/3", "3:4": "3/4" };
const field = "w-full rounded-lg border border-white/10 bg-[#0B0B0B] px-3 py-2 text-sm text-white outline-none transition-colors placeholder:text-white/25 focus:border-[#FF3B3B]/60";
const errMsg = (e, d) => { const x = e?.response?.data?.detail; return (typeof x === "string" ? x : x?.message) || d; };

function Section({ icon: Icon, title, children, right }) {
  return (
    <div className="border-t border-white/[0.06] py-5 first:border-t-0 first:pt-0">
      <div className="mb-3 flex items-center gap-2"><Icon size={14} className="text-[#FF3B3B]" /><h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/60">{title}</h3><div className="ml-auto">{right}</div></div>
      {children}
    </div>
  );
}

const Chip = ({ on, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-[#FF3B3B] text-white" : "border border-white/10 text-white/60 hover:border-white/30 hover:text-white"}`}>{children}</button>
);

function ProLock({ onUpgrade }) {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-[#0A0A0A]/80 backdrop-blur-md" data-testid="yt-pro-lock">
      <div className="max-w-sm rounded-2xl border border-[#F59E0B]/30 bg-[#111] p-7 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#F59E0B]/15"><Lock className="text-[#F59E0B]" size={20} /></div>
        <h2 className="font-display text-xl font-black">A Pro tool</h2>
        <p className="mt-2 text-sm text-white/50">Upgrade to Pro to design YouTube thumbnails with your face, poses, objects and AI touch-ups.</p>
        <button onClick={onUpgrade} data-testid="yt-upgrade-btn" className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#F59E0B] px-5 py-2.5 text-sm font-bold text-black hover:bg-[#fbbf24]"><Crown size={15} /> Upgrade to Pro</button>
      </div>
    </div>
  );
}

export default function YoutubeThumbnail() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [aspect, setAspect] = useState("16:9");
  const [category, setCategory] = useState("");
  const [style, setStyle] = useState("");
  const [pose, setPose] = useState("");
  const [anchor, setAnchor] = useState(null);
  const [objects, setObjects] = useState([null]);
  const [title, setTitle] = useState("");
  const [tagline, setTagline] = useState("");
  const [prompt, setPrompt] = useState("");
  const [active, setActive] = useState("anchor");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [edit, setEdit] = useState("");
  const [editing, setEditing] = useState(false);
  const [galleryKey, setGalleryKey] = useState(0);

  useEffect(() => { api.ytConfig().then((c) => { setCfg(c); if (c.aspects[0] && !c.aspects.some((a) => a.id === "16:9")) setAspect(c.aspects[0].id); }).catch(() => setCfg({ error: true })); }, []);
  const f = cfg?.features || {};
  const locked = cfg && !cfg.error && !cfg.access.allow;

  const setObj = (i, v) => setObjects((o) => o.map((x, j) => (j === i ? v : x)));
  // Ctrl/Cmd+V anywhere → drops the image into the active slot (or the next empty one).
  const onPaste = useCallback(async (e) => {
    if (!f.paste || locked) return;
    const item = [...(e.clipboardData?.items || [])].find((it) => it.type.startsWith("image/"));
    if (!item) return;
    e.preventDefault();
    const file = item.getAsFile();
    try {
      let target = active;
      if (target === "anchor" && (!f.anchor || anchor)) target = "obj0";
      if (target.startsWith("obj")) {
        const empty = objects.findIndex((x) => !x);
        const idx = Number(target.slice(3));
        target = objects[idx] ? (empty >= 0 ? `obj${empty}` : null) : target;
      }
      if (!target || (target.startsWith("obj") && !f.objects)) { toast.error("All image slots are full."); return; }
      toast.message("Pasting image…");
      const up = await uploadThumbImage(file, target === "anchor" ? "anchor" : "object");
      if (target === "anchor") setAnchor(up); else setObj(Number(target.slice(3)), up);
    } catch (err) { toast.error(errMsg(err, err.message)); }
  }, [f, locked, active, anchor, objects]);
  useEffect(() => { window.addEventListener("paste", onPaste); return () => window.removeEventListener("paste", onPaste); }, [onPaste]);

  const show = (g) => { setResult(g); setHistory((h) => [g, ...h.filter((x) => x.id !== g.id)].slice(0, 12)); };
  const generate = async () => {
    setBusy(true);
    try {
      const g = await api.ytGenerate({ aspect, category, style, pose: anchor ? pose : "", title, tagline, prompt: f.ai_prompt ? prompt : "",
        anchor_path: anchor?.path || null, object_paths: objects.filter(Boolean).map((o) => o.path) });
      show(g); setGalleryKey((k) => k + 1); toast.success("Thumbnail ready");
    } catch (e) {
      if (e?.response?.status === 402) { nav("/pricing"); return; }
      toast.error(errMsg(e, "Generation failed"));
    }
    setBusy(false);
  };
  const applyEdit = async () => {
    if (!edit.trim() || !result) return;
    setEditing(true);
    try { const g = await api.ytEdit(result.id, edit.trim()); show(g); setEdit(""); setGalleryKey((k) => k + 1); toast.success("Edit applied"); }
    catch (e) { toast.error(errMsg(e, "Edit failed")); }
    setEditing(false);
  };

  if (!cfg) return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A]"><Loader2 className="animate-spin text-[#FF3B3B]" /></div>;
  if (cfg.error) return <div className="min-h-screen bg-[#0A0A0A] text-white"><Navbar /><p className="py-24 text-center text-white/50" data-testid="yt-unavailable">The YouTube Thumbnail Designer is not available right now.</p></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="yt-thumbnail-page">
      <Navbar />
      <div className="mx-auto max-w-[1400px] px-5 pb-20 pt-10 sm:px-8">
        <div className="mb-8 flex flex-wrap items-end gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#FF3B3B]/30 bg-[#FF3B3B]/10 px-3 py-1 text-[11px] font-bold text-[#ff7a7a]"><Youtube size={13} /> YouTube Thumbnail Designer <Crown size={11} className="text-[#F59E0B]" /></span>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tight sm:text-5xl">Thumbnails that get <span className="text-[#FF3B3B]">clicked.</span></h1>
            <p className="mt-2 max-w-xl text-sm text-white/45">Drop in your face and products, pick a style and a pose, write a big title — AI designs it. Then touch it up by just describing the change.</p>
          </div>
        </div>

        <div className="relative grid gap-6 lg:grid-cols-[420px_1fr]">
          {locked && <ProLock onUpgrade={() => nav("/pricing")} />}
          <aside className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-5 lg:sticky lg:top-6 lg:max-h-[calc(100vh-48px)] lg:overflow-y-auto" data-testid="yt-controls">
            <Section icon={Youtube} title="Format">
              <div className="flex flex-wrap gap-1.5">{cfg.aspects.map((a) => <Chip key={a.id} on={aspect === a.id} onClick={() => setAspect(a.id)} testid={`yt-aspect-${a.id}`}>{a.label}</Chip>)}</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="yt-category" className={`${field} mt-3`}>
                <option value="">Any category</option>{cfg.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Section>
            <Section icon={Palette} title="Style preset">
              <div className="flex flex-wrap gap-1.5">
                <Chip on={!style} onClick={() => setStyle("")} testid="yt-style-auto">Auto</Chip>
                {cfg.styles.map((s) => <Chip key={s.id} on={style === s.id} onClick={() => setStyle(s.id)} testid={`yt-style-${s.id}`}>{s.name}</Chip>)}
              </div>
            </Section>
            {f.anchor && (
              <Section icon={User} title="Anchor face">
                <ImageSlot label="Add anchor photo" hint="Clear, front-facing face works best" value={anchor} onChange={setAnchor} role="anchor" tall
                  canPaste={f.paste} active={active === "anchor"} onFocus={() => setActive("anchor")} testid="yt-anchor" />
                {f.poses && anchor && (
                  <div className="mt-3">
                    <div className="mb-2 text-[11px] font-semibold text-white/45">Pose & expression</div>
                    <div className="flex flex-wrap gap-1.5">
                      <Chip on={!pose} onClick={() => setPose("")} testid="yt-pose-none">Natural</Chip>
                      {cfg.poses.map((p) => <Chip key={p.id} on={pose === p.id} onClick={() => setPose(p.id)} testid={`yt-pose-${p.id}`}>{p.name}</Chip>)}
                    </div>
                  </div>
                )}
              </Section>
            )}
            {f.objects && (
              <Section icon={Package} title="Object images" right={objects.length < cfg.max_objects && (
                <button onClick={() => setObjects((o) => [...o, null])} data-testid="yt-add-object" className="flex items-center gap-1 text-[11px] font-bold text-[#FF3B3B] hover:text-[#ff7a7a]"><Plus size={12} /> Add</button>)}>
                <div className="grid grid-cols-2 gap-2">
                  {objects.map((o, i) => (
                    <ImageSlot key={i} label={`Object ${i + 1}`} hint="Product, logo, prop…" value={o} onChange={(v) => setObj(i, v)} role="object"
                      canPaste={f.paste} active={active === `obj${i}`} onFocus={() => setActive(`obj${i}`)} testid={`yt-object-${i}`} />
                  ))}
                </div>
                {f.paste && <p className="mt-2 text-[10px] text-white/30">Tip: press Ctrl+V / ⌘V anywhere to paste an image into the highlighted slot.</p>}
              </Section>
            )}
            <Section icon={Type} title="Text">
              <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, cfg.title_max))} placeholder="BIG TITLE — e.g. I SURVIVED 7 DAYS" data-testid="yt-title" className={`${field} font-display text-base font-black uppercase tracking-wide`} />
              <div className="mt-1 text-right text-[10px] text-white/25">{title.length}/{cfg.title_max}</div>
              <input value={tagline} onChange={(e) => setTagline(e.target.value.slice(0, cfg.tagline_max))} placeholder="Tag line (optional)" data-testid="yt-tagline" className={field} />
            </Section>
            {f.ai_prompt && (
              <Section icon={Sparkles} title="Creative prompt">
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} placeholder="Describe the scene, mood, colours… e.g. 'exploding volcano behind me, red sky, arrow pointing at the phone'" data-testid="yt-prompt" className={`${field} resize-none`} />
              </Section>
            )}
            <button onClick={generate} disabled={busy || locked} data-testid="yt-generate-btn"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#FF3B3B] py-3 text-sm font-black text-white shadow-[0_8px_30px_-8px_rgba(255,59,59,0.6)] transition-transform hover:scale-[1.01] disabled:opacity-50">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Designing…</> : <><Sparkles size={16} /> Generate thumbnail</>}
            </button>
          </aside>

          <main data-testid="yt-preview-panel">
            <div className="flex items-center justify-center rounded-2xl border border-white/10 bg-[radial-gradient(ellipse_at_top,#1a0b0b,transparent_60%)] p-6">
              <div className="relative w-full overflow-hidden rounded-xl border border-white/10 bg-black" style={{ aspectRatio: RATIO[result?.aspect || aspect] || "16/9", maxHeight: "68vh", maxWidth: (result?.aspect || aspect) === "16:9" ? "100%" : "56vh" }}>
                {result ? <img src={abs(result.result_url)} alt="Thumbnail" className="h-full w-full object-contain" data-testid="yt-result-image" /> : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center text-white/25" data-testid="yt-empty-preview">
                    {busy ? <Loader2 size={28} className="animate-spin text-[#FF3B3B]" /> : <Youtube size={34} />}
                    <span className="text-sm">{busy ? "Designing your thumbnail…" : `${aspect} preview`}</span>
                  </div>
                )}
                {(busy || editing) && result && <div className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 size={28} className="animate-spin text-[#FF3B3B]" /></div>}
              </div>
            </div>
            {result && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {f.touch_edit && (
                  <div className="flex min-w-[260px] flex-1 items-center gap-2 rounded-full border border-white/10 bg-[#0F0F0F] py-1.5 pl-4 pr-1.5" data-testid="yt-touch-edit">
                    <Wand2 size={14} className="text-[#FF3B3B]" />
                    <input value={edit} onChange={(e) => setEdit(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyEdit()} placeholder="Touch & Edit — 'make the title yellow', 'add fire behind me'…" data-testid="yt-edit-input" className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30" />
                    <button onClick={applyEdit} disabled={editing || !edit.trim()} data-testid="yt-edit-apply" className="rounded-full bg-white px-4 py-1.5 text-xs font-bold text-black disabled:opacity-40">{editing ? "Editing…" : "Apply"}</button>
                  </div>
                )}
                <a href={mediaUrl(result.result_path, { download: true, name: "youtube-thumbnail.png" })} data-testid="yt-download-btn"
                  className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-bold text-white hover:bg-white/10"><Download size={14} /> Download PNG</a>
              </div>
            )}
            {history.length > 1 && (
              <div className="mt-4 flex gap-2 overflow-x-auto pb-1" data-testid="yt-session-history">
                {history.map((g) => (
                  <button key={g.id} onClick={() => setResult(g)} className={`shrink-0 overflow-hidden rounded-lg border ${result?.id === g.id ? "border-[#FF3B3B]" : "border-white/10"}`}>
                    <img src={abs(g.result_url)} alt="" className="h-16 w-28 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </main>
        </div>
        {f.gallery && <ThumbGallery refreshKey={galleryKey} onOpen={(g) => { show(g); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}
      </div>
    </div>
  );
}
